import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { chain, map, isNil } from 'lodash';
import { useOnClickOutside } from 'taboola-ultimate-ui';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { NewNotificationsIndicator } from './NewNotificationsIndicator/NewNotificationsIndicator';
import { NotificationCardsBySubType } from './config/NotificationCardsBySubType';
import { NOTIFICATION_STATE } from './config/NotificationState';
import { useNotificationsActions } from './hooks/useNotificationsActions';
import { useNotificationsService } from './hooks/useNotificationsService';
import styles from './notificationsCenter.module.scss';

const classNameBuilder = classnames.bind(styles);

export const NotificationsCenter = ({ open: openProp, headerClassName, dropdownClassName }) => {
    const ref = useRef(null);
    const headless = !isNil(openProp);
    const [openState, setOpen] = useState(false);
    const open = headless ? openProp : openState;
    const { notifications = [], refetch } = useNotificationsService();

    const closeNotificationsCenter = useCallback(() => {
        if (!open) {
            return;
        }
        setOpen(false);
        refetch();
    }, [open, refetch]);

    useOnClickOutside(ref, closeNotificationsCenter);

    const { accountName } = useAccount();

    const newNotificationsCount = useMemo(
        () => chain(notifications).filter({ state: NOTIFICATION_STATE.NONE }).size().value(),
        [notifications]
    );

    const { onView } = useNotificationsActions({
        viewParams: {
            errorCode: 'app.notificationsCenter.view.error-message',
            notificationIds: map(notifications, 'id'),
            accountName,
        },
    });

    const hasNotifications = notifications.length > 0;

    const toggleNotificationsCenter = useCallback(() => {
        setOpen(open => !open);
    }, [setOpen]);

    useEffect(() => {
        if (open && hasNotifications) {
            onView();
        }
    }, [open, onView, hasNotifications]);

    const dropdownContent = open && (
        <>
            <div className={classNameBuilder('notification-center-header', headerClassName)}>
                <FormattedMessage id="app.notificationsCenter.header" defaultMessage="Notifications" />
            </div>
            <div className={classNameBuilder('notifications-center-dropdown', dropdownClassName)}>
                {!hasNotifications && (
                    <div className={styles['empty-notifications-state']}>
                        <FormattedMessage
                            id="app.notificationsCenter.emptyNotifications"
                            defaultMessage="No Notifications yet"
                        />
                    </div>
                )}
                {notifications.map(notification => {
                    const NotificationCard = NotificationCardsBySubType[notification.subType];

                    const timeDiffInMinutes = (Date.now() - notification.createTimeInUtc) / 1000 / 60;

                    return (
                        <NotificationCard
                            key={notification.id}
                            notification={notification}
                            timeDiffInMinutes={timeDiffInMinutes}
                            closeNotificationCenter={closeNotificationsCenter}
                        />
                    );
                })}
            </div>
        </>
    );

    if (headless) {
        return dropdownContent;
    }

    return (
        <div ref={ref} aria-label="Notifications Center" className={styles['notifications-center']}>
            <NewNotificationsIndicator
                newNotificationsCount={newNotificationsCount}
                open={open}
                toggleNotificationsCenter={toggleNotificationsCenter}
            />
            {dropdownContent}
        </div>
    );
};

NotificationsCenter.propTypes = {};
